"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { JSONUIProvider, Renderer, type Spec } from "@json-render/react";
import {
  Search,
  BookOpen,
  Folder,
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLesson } from "@/contexts/lesson-context";
import { getAllLessons, type MindUPLesson } from "@/data/curriculum";
import { MINI_LESSONS, type MiniLesson } from "@/data/mini-lessons";
import Image from "next/image";

interface SidebarProps {
  open: boolean;
  onToggle: () => void;
}

const GRADE_ORDER = ["K-2", "3-5", "6-8"] as const;
type GradeBand = (typeof GRADE_ORDER)[number];

const gradeFromId = (id: string) => {
  const m = id?.match(/^(k2|35|68|pk|g3-5|g6-8)[-_]/i);
  if (!m) return "K-2";
  const key = m[1].toLowerCase();
  const map: Record<string, string> = {
    k2: "K-2",
    "35": "3-5",
    "68": "6-8",
    "g3-5": "3-5",
    "g6-8": "6-8",
    pk: "Pre-K",
  };
  return map[key] ?? "K-2";
};

// Convert MindUP lesson to Lesson context format
function convertToLessonContext(lesson: MindUPLesson) {
  return {
    id: lesson.id,
    title: lesson.title,
    description: lesson.purpose,
    gradeLevel: lesson.gradeLevel ?? gradeFromId(lesson.id),
    duration: lesson.estimatedTime,
    content: lesson.content,
    unit: lesson.unit,
    lessonNumber: lesson.lessonNumber,
    concepts: lesson.concepts,
    objectives: lesson.objectives,
    materials: lesson.materials,
    socialEmotionalLearning: lesson.socialEmotionalLearning,
  };
}

function getByPath(source: unknown, path: string): unknown {
  if (!source || typeof source !== "object" || !path) return undefined;
  const segments = path.split(".").filter(Boolean);
  let current: unknown = source;
  for (const segment of segments) {
    if (!current || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function setByPath(
  source: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> {
  const output = structuredClone(source);
  const segments = path.split(".").filter(Boolean);
  if (segments.length === 0) return output;
  let cursor: Record<string, unknown> = output;
  for (let i = 0; i < segments.length - 1; i++) {
    const key = segments[i];
    const current = cursor[key];
    if (!current || typeof current !== "object" || Array.isArray(current)) {
      cursor[key] = {};
    }
    cursor = cursor[key] as Record<string, unknown>;
  }
  cursor[segments[segments.length - 1]] = value;
  return output;
}

function buildMiniLessonEditorSpec(lesson: MiniLesson | null): Spec | null {
  if (!lesson) return null;

  const elements: Record<string, { type: string; props: Record<string, unknown>; children: string[] }> = {};

  const add = (
    key: string,
    type: string,
    props: Record<string, unknown>,
    children: string[] = [],
  ) => {
    elements[key] = { type, props, children };
  };

  add("root", "Section", {}, ["basic", "content", "advanced"]);

  add("basic", "Section", { title: "Basic Info" }, [
    "field-title",
    "field-duration",
    "field-video",
    "field-description",
  ]);
  add("field-title", "TextField", { label: "Title", path: "title" });
  add("field-duration", "TextField", { label: "Duration", path: "duration" });
  add("field-video", "TextField", { label: "Video URL", path: "video" });
  add("field-description", "TextAreaField", {
    label: "Description",
    path: "description",
    rows: 3,
  });

  add("content", "Section", { title: "Core Content" }, [
    "field-grade-level",
    "field-estimated-time",
    "field-purpose",
    "field-key-concepts",
  ]);

  add("field-grade-level", "TextField", {
    label: "Grade Level",
    path: "content.gradeLevel",
  });
  add("field-estimated-time", "TextField", {
    label: "Estimated Time",
    path: "content.estimatedTime",
  });
  add("field-purpose", "TextAreaField", {
    label: "Purpose",
    path: "content.purpose",
    rows: 4,
  });
  add("field-key-concepts", "LinesField", {
    label: "Key Concepts (one per line)",
    path: "content.keyConcepts",
    rows: 4,
  });

  add("advanced", "Section", { title: "Advanced Content" }, [
    "field-prep",
    "field-read-aloud",
    "field-outline",
  ]);

  add("field-prep", "LinesField", {
    label: "Preparation Materials (one per line)",
    path: "content.preparationMaterials",
    rows: 4,
  });
  add("field-read-aloud", "ScriptEditor", {
    label: "Read Aloud Script",
    path: "content.readAloudScript",
  });
  add("field-outline", "OutlineEditor", {
    label: "Lesson Outline",
    path: "content.lessonOutline",
  });

  const knownTop = new Set([
    "id",
    "title",
    "video",
    "description",
    "duration",
    "content",
  ]);
  const knownContent = new Set([
    "gradeLevel",
    "estimatedTime",
    "purpose",
    "keyConcepts",
    "preparationMaterials",
    "readAloudScript",
    "lessonOutline",
  ]);

  const extraTop = Object.keys(lesson).filter((key) => !knownTop.has(key));
  const extraContent = Object.keys(lesson.content ?? {}).filter(
    (key) => !knownContent.has(key),
  );

  const extraChildren: string[] = [];

  extraTop.forEach((key) => {
    const fieldKey = `extra-top-${key}`;
    add(fieldKey, "JsonField", {
      label: `Extra Field: ${key}`,
      path: key,
      rows: 6,
    });
    extraChildren.push(fieldKey);
  });

  extraContent.forEach((key) => {
    const fieldKey = `extra-content-${key}`;
    add(fieldKey, "JsonField", {
      label: `Extra Content Field: ${key}`,
      path: `content.${key}`,
      rows: 6,
    });
    extraChildren.push(fieldKey);
  });

  if (extraChildren.length > 0) {
    add("extra", "Section", { title: "Additional Fields" }, extraChildren);
    elements.root.children.push("extra");
  }

  return {
    root: "root",
    elements,
  };
}

export function Sidebar(_props: SidebarProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTocLesson, setSelectedTocLesson] = useState<string | null>(null);
  const [expandedGrades, setExpandedGrades] = useState<Set<GradeBand>>(new Set(["K-2"]));
  const [expandedUnits, setExpandedUnits] = useState<Set<string>>(new Set());
  const { setCurrentLesson, setCurrentMiniLesson, currentMiniLesson } = useLesson();
  const [miniLessons, setMiniLessons] = useState<MiniLesson[]>(MINI_LESSONS);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminStatus, setAdminStatus] = useState<string | null>(null);
  const [isSavingLesson, setIsSavingLesson] = useState(false);
  const [isDeletingLessonId, setIsDeletingLessonId] = useState<string | null>(null);
  const [miniLessonActionStatus, setMiniLessonActionStatus] = useState<string | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingLessonId, setEditingLessonId] = useState<string | null>(null);
  const [editorLesson, setEditorLesson] = useState<MiniLesson | null>(null);
  const [jsonFieldDrafts, setJsonFieldDrafts] = useState<Record<string, string>>({});
  const editorLessonRef = useRef<MiniLesson | null>(null);
  const jsonFieldDraftsRef = useRef<Record<string, string>>({});

  // Get all lessons and group by grade + unit.
  const lessonsByGradeAndUnit = useMemo(() => {
    const allLessons = getAllLessons();
    const grouped: Record<GradeBand, Record<number, { unitTitle: string; lessons: MindUPLesson[] }>> = {
      "K-2": {},
      "3-5": {},
      "6-8": {},
    };

    allLessons.forEach((lesson) => {
      const gradeLevel = (lesson.gradeLevel ?? gradeFromId(lesson.id)) as GradeBand;
      const grade = GRADE_ORDER.includes(gradeLevel) ? gradeLevel : "K-2";

      if (!grouped[grade][lesson.unit]) {
        grouped[grade][lesson.unit] = {
          unitTitle: lesson.unitTitle,
          lessons: [],
        };
      }
      grouped[grade][lesson.unit].lessons.push(lesson);
    });

    return grouped;
  }, []);

  const filteredLessonsByGradeAndUnit = useMemo(() => {
    if (!searchQuery) return lessonsByGradeAndUnit;

    const filtered: Record<GradeBand, Record<number, { unitTitle: string; lessons: MindUPLesson[] }>> = {
      "K-2": {},
      "3-5": {},
      "6-8": {},
    };
    const lowerQuery = searchQuery.toLowerCase();

    GRADE_ORDER.forEach((grade) => {
      Object.entries(lessonsByGradeAndUnit[grade]).forEach(([unit, data]) => {
        const matchingLessons = data.lessons.filter(
          (lesson) =>
            lesson.title.toLowerCase().includes(lowerQuery) ||
            lesson.purpose.toLowerCase().includes(lowerQuery) ||
            lesson.concepts.some((c) => c.toLowerCase().includes(lowerQuery))
        );

        if (matchingLessons.length > 0) {
          filtered[grade][Number(unit)] = {
            unitTitle: data.unitTitle,
            lessons: matchingLessons,
          };
        }
      });
    });

    return filtered;
  }, [searchQuery, lessonsByGradeAndUnit]);

  const filteredMiniLessons = useMemo(() => {
    if (!searchQuery) return miniLessons;

    const lowerQuery = searchQuery.toLowerCase();

    return miniLessons.filter((lesson) => {
      const searchable = [
        lesson.id,
        lesson.title,
        lesson.description,
        lesson.duration,
        lesson.content?.gradeLevel,
        lesson.content?.estimatedTime,
        lesson.content?.purpose,
        ...(lesson.content?.keyConcepts ?? []),
        ...(lesson.content?.preparationMaterials ?? []),
        ...(lesson.content?.lessonOutline?.flatMap((section) => [
          section.title,
          section.duration,
          ...(section.parts?.flatMap((part) => [part.label, part.text]) ?? []),
        ]) ?? []),
        ...(lesson.content?.readAloudScript?.map((line) => line.text) ?? []),
      ]
        .filter((value): value is string => typeof value === "string")
        .map((value) => value.toLowerCase());

      return searchable.some((value) => value.includes(lowerQuery));
    });
  }, [miniLessons, searchQuery]);

  const lessonCountsByGrade = useMemo(() => {
    const counts: Record<GradeBand, number> = {
      "K-2": 0,
      "3-5": 0,
      "6-8": 0,
    };

    GRADE_ORDER.forEach((grade) => {
      counts[grade] = Object.values(filteredLessonsByGradeAndUnit[grade]).reduce((total, unitData) => {
        return total + unitData.lessons.length;
      }, 0);
    });

    return counts;
  }, [filteredLessonsByGradeAndUnit]);

  const toggleGrade = (grade: GradeBand) => {
    setExpandedGrades((prev) => {
      const next = new Set(prev);
      if (next.has(grade)) {
        next.delete(grade);
      } else {
        next.add(grade);
      }
      return next;
    });
  };

  const toggleUnit = (grade: GradeBand, unit: number) => {
    const key = `${grade}-${unit}`;
    setExpandedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const isUnitExpanded = (grade: GradeBand, unit: number) => {
    return expandedUnits.has(`${grade}-${unit}`);
  };

  const hasGradeLessons = (grade: GradeBand) => lessonCountsByGrade[grade] > 0;
  
  const handleTocLessonClick = (lesson: MindUPLesson) => {
    setSelectedTocLesson(lesson.id);
    setCurrentMiniLesson(null);
    const lessonContext = convertToLessonContext(lesson);
    setCurrentLesson(lessonContext);
  };

  const handleMiniLessonClick = (miniLesson: MiniLesson) => {
    setSelectedTocLesson(null);
    setCurrentLesson(null);
    setCurrentMiniLesson(miniLesson);
  };

  const refreshMiniLessons = async () => {
    try {
      const response = await fetch("/api/mini-lessons", { cache: "no-store" });
      if (!response.ok) return;

      const payload = (await response.json()) as MiniLesson[];
      if (!Array.isArray(payload)) return;

      setMiniLessons(payload);
      if (currentMiniLesson) {
        const updatedCurrent = payload.find((lesson) => lesson.id === currentMiniLesson.id);
        if (updatedCurrent) {
          setCurrentMiniLesson(updatedCurrent);
        }
      }
    } catch {
      // Keep seed data if API isn't available.
    }
  };

  useEffect(() => {
    refreshMiniLessons();
  }, []);

  const refreshAdminSession = async () => {
    try {
      const response = await fetch("/api/admin/session", { cache: "no-store" });
      const result = (await response.json().catch(() => ({}))) as {
        isAdmin?: boolean;
      };
      setIsAdmin(Boolean(response.ok && result.isAdmin));
    } catch {
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    refreshAdminSession();
  }, []);

  const openEditDialog = (lesson: MiniLesson) => {
    const cloned = structuredClone(lesson);
    setEditingLessonId(lesson.id);
    setEditorLesson(cloned);
    editorLessonRef.current = cloned;
    setJsonFieldDrafts({});
    jsonFieldDraftsRef.current = {};
    setAdminStatus(null);
    setMiniLessonActionStatus(null);
    setIsEditorOpen(true);
  };

  const deleteMiniLesson = async (lesson: MiniLesson) => {
    const confirmed = window.confirm(
      `Delete mini lesson "${lesson.title}"? This cannot be undone.`,
    );
    if (!confirmed) return;

    setIsDeletingLessonId(lesson.id);
    setMiniLessonActionStatus(null);
    try {
      const response = await fetch(
        `/api/mini-lessons/${encodeURIComponent(lesson.id)}`,
        {
          method: "DELETE",
        },
      );
      const result = (await response.json().catch(() => ({}))) as {
        error?: string;
      };
      if (!response.ok) {
        setMiniLessonActionStatus(result.error ?? "Failed to delete mini lesson.");
        return;
      }

      if (currentMiniLesson?.id === lesson.id) {
        setCurrentMiniLesson(null);
      }
      await refreshMiniLessons();
      setMiniLessonActionStatus("Mini lesson deleted.");
    } catch {
      setMiniLessonActionStatus("Failed to delete mini lesson.");
    } finally {
      setIsDeletingLessonId(null);
    }
  };

  const updateEditorField = (path: string, value: string) => {
    setEditorLesson((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev) as Record<string, unknown>;
      const segments = path.split(".").filter(Boolean);
      let cursor = next;
      for (let i = 0; i < segments.length - 1; i++) {
        const key = segments[i];
        const current = cursor[key];
        if (!current || typeof current !== "object" || Array.isArray(current)) {
          cursor[key] = {};
        }
        cursor = cursor[key] as Record<string, unknown>;
      }
      cursor[segments[segments.length - 1]] = value;
      const nextLesson = next as MiniLesson;
      editorLessonRef.current = nextLesson;
      return nextLesson;
    });
  };

  const updateEditorLinesField = (path: string, value: string) => {
    const lines = value
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    setEditorLesson((prev) => {
      if (!prev) return prev;
      const nextLesson = setByPath(
        prev as Record<string, unknown>,
        path,
        lines,
      ) as MiniLesson;
      editorLessonRef.current = nextLesson;
      return nextLesson;
    });
  };

  const applyJsonDraft = (path: string, rawValue: string): boolean => {
    try {
      const parsed = JSON.parse(rawValue);
      setEditorLesson((prev) => {
        if (!prev) return prev;
        const nextLesson = setByPath(
          prev as Record<string, unknown>,
          path,
          parsed,
        ) as MiniLesson;
        editorLessonRef.current = nextLesson;
        return nextLesson;
      });
      setAdminStatus(null);
      return true;
    } catch {
      setAdminStatus(`Invalid JSON for field: ${path}`);
      return false;
    }
  };

  const editorSpec = useMemo(
    () => buildMiniLessonEditorSpec(editorLesson),
    [editorLesson],
  );

  useEffect(() => {
    editorLessonRef.current = editorLesson;
  }, [editorLesson]);

  useEffect(() => {
    jsonFieldDraftsRef.current = jsonFieldDrafts;
  }, [jsonFieldDrafts]);

  const editorRegistry = useMemo(
    () => ({
      Section: ({
        element,
        children,
      }: {
        element: { props?: { title?: string; description?: string } };
        children?: React.ReactNode;
      }) => (
        <div className="space-y-2 rounded-lg border border-[var(--mindup-navy)]/10 bg-white p-3">
          {element.props?.title && (
            <p className="text-sm font-semibold text-[var(--mindup-navy)]">
              {element.props.title}
            </p>
          )}
          {element.props?.description && (
            <p className="text-xs text-[var(--mindup-soft-black)]/70">{element.props.description}</p>
          )}
          <div className="space-y-2">{children}</div>
        </div>
      ),
      TextField: ({
        element,
      }: {
        element: { props?: { label?: string; path?: string; placeholder?: string } };
      }) => {
        const path = element.props?.path ?? "";
        const current = getByPath(editorLessonRef.current, path);
        return (
          <div className="space-y-1">
            <p className="text-xs font-medium">{element.props?.label}</p>
            <Input
              value={typeof current === "string" || typeof current === "number" ? String(current) : ""}
              placeholder={element.props?.placeholder}
              onChange={(event) => updateEditorField(path, event.target.value)}
            />
          </div>
        );
      },
      TextAreaField: ({
        element,
      }: {
        element: {
          props?: { label?: string; path?: string; rows?: number; placeholder?: string };
        };
      }) => {
        const path = element.props?.path ?? "";
        const current = getByPath(editorLessonRef.current, path);
        return (
          <div className="space-y-1">
            <p className="text-xs font-medium">{element.props?.label}</p>
            <Textarea
              value={typeof current === "string" ? current : ""}
              rows={element.props?.rows}
              placeholder={element.props?.placeholder}
              onChange={(event) => updateEditorField(path, event.target.value)}
            />
          </div>
        );
      },
      LinesField: ({
        element,
      }: {
        element: { props?: { label?: string; path?: string; rows?: number } };
      }) => {
        const path = element.props?.path ?? "";
        const current = getByPath(editorLessonRef.current, path);
        const lines = Array.isArray(current) ? current.map((v) => String(v)).join("\n") : "";
        return (
          <div className="space-y-1">
            <p className="text-xs font-medium">{element.props?.label}</p>
            <Textarea
              value={lines}
              rows={element.props?.rows}
              onChange={(event) => updateEditorLinesField(path, event.target.value)}
            />
          </div>
        );
      },
      ScriptEditor: ({
        element,
      }: {
        element: { props?: { label?: string; path?: string } };
      }) => {
        type ScriptLine = { kind: "teacher" | "direction"; text: string };
        const path = element.props?.path ?? "";
        const current = getByPath(editorLessonRef.current, path);
        const rows: ScriptLine[] = Array.isArray(current)
          ? current
              .map((item) =>
                item && typeof item === "object"
                  ? {
                      kind:
                        (item as { kind?: string }).kind === "direction"
                          ? "direction"
                          : "teacher",
                      text: String((item as { text?: unknown }).text ?? ""),
                    }
                  : { kind: "teacher", text: "" },
              )
          : [];

        const setRows = (nextRows: ScriptLine[]) => {
          setEditorLesson((prev) => {
            if (!prev) return prev;
            return setByPath(
              prev as Record<string, unknown>,
              path,
              nextRows,
            ) as MiniLesson;
          });
        };

        return (
          <div className="space-y-2">
            <p className="text-xs font-medium">{element.props?.label}</p>
            {rows.map((row, idx) => (
              <div
                key={`${path}-${idx}`}
                className="rounded-md border border-[var(--mindup-navy)]/10 p-2 space-y-2"
              >
                <div className="flex items-center gap-2">
                  <select
                    className="h-8 rounded-md border border-[var(--mindup-navy)]/20 px-2 text-xs"
                    value={row.kind}
                    onChange={(event) => {
                      const kind = event.target.value === "direction" ? "direction" : "teacher";
                      const next = [...rows];
                      next[idx] = { ...next[idx], kind };
                      setRows(next);
                    }}
                  >
                    <option value="teacher">Teacher</option>
                    <option value="direction">Direction</option>
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const next = rows.filter((_, i) => i !== idx);
                      setRows(next);
                    }}
                  >
                    Remove
                  </Button>
                </div>
                <Textarea
                  value={row.text}
                  rows={2}
                  onChange={(event) => {
                    const next = [...rows];
                    next[idx] = { ...next[idx], text: event.target.value };
                    setRows(next);
                  }}
                />
              </div>
            ))}
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setRows([...rows, { kind: "teacher", text: "" }]);
              }}
            >
              Add Script Line
            </Button>
          </div>
        );
      },
      OutlineEditor: ({
        element,
      }: {
        element: { props?: { label?: string; path?: string } };
      }) => {
        const path = element.props?.path ?? "";
        const current = getByPath(editorLessonRef.current, path);
        const sections = Array.isArray(current)
          ? current.map((section, idx) => {
              const s =
                section && typeof section === "object"
                  ? (section as Record<string, unknown>)
                  : {};
              const partsRaw = Array.isArray(s.parts) ? s.parts : [];
              return {
                number:
                  typeof s.number === "number" ? s.number : idx + 1,
                title: typeof s.title === "string" ? s.title : "",
                duration: typeof s.duration === "string" ? s.duration : "",
                parts: partsRaw.map((part) => {
                  const p =
                    part && typeof part === "object"
                      ? (part as Record<string, unknown>)
                      : {};
                  return {
                    label: typeof p.label === "string" ? p.label : "",
                    text: typeof p.text === "string" ? p.text : "",
                  };
                }),
              };
            })
          : [];

        const setSections = (
          nextSections: Array<{
            number: number;
            title: string;
            duration: string;
            parts: Array<{ label: string; text: string }>;
          }>,
        ) => {
          setEditorLesson((prev) => {
            if (!prev) return prev;
            return setByPath(
              prev as Record<string, unknown>,
              path,
              nextSections,
            ) as MiniLesson;
          });
        };

        return (
          <div className="space-y-2">
            <p className="text-xs font-medium">{element.props?.label}</p>
            {sections.map((section, sIdx) => (
              <div
                key={`${path}-section-${sIdx}`}
                className="rounded-md border border-[var(--mindup-navy)]/10 p-3 space-y-2"
              >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input
                    type="number"
                    value={section.number}
                    onChange={(event) => {
                      const next = [...sections];
                      next[sIdx] = {
                        ...next[sIdx],
                        number: Number(event.target.value || sIdx + 1),
                      };
                      setSections(next);
                    }}
                    placeholder="Step #"
                  />
                  <Input
                    value={section.title}
                    onChange={(event) => {
                      const next = [...sections];
                      next[sIdx] = { ...next[sIdx], title: event.target.value };
                      setSections(next);
                    }}
                    placeholder="Section title"
                  />
                  <Input
                    value={section.duration}
                    onChange={(event) => {
                      const next = [...sections];
                      next[sIdx] = { ...next[sIdx], duration: event.target.value };
                      setSections(next);
                    }}
                    placeholder="Duration"
                  />
                </div>

                <div className="space-y-2">
                  {section.parts.map((part, pIdx) => (
                    <div
                      key={`${path}-section-${sIdx}-part-${pIdx}`}
                      className="grid grid-cols-1 sm:grid-cols-[160px_1fr_auto] gap-2"
                    >
                      <Input
                        value={part.label}
                        onChange={(event) => {
                          const next = [...sections];
                          next[sIdx].parts[pIdx] = {
                            ...next[sIdx].parts[pIdx],
                            label: event.target.value,
                          };
                          setSections(next);
                        }}
                        placeholder="Label"
                      />
                      <Input
                        value={part.text}
                        onChange={(event) => {
                          const next = [...sections];
                          next[sIdx].parts[pIdx] = {
                            ...next[sIdx].parts[pIdx],
                            text: event.target.value,
                          };
                          setSections(next);
                        }}
                        placeholder="Text"
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const next = [...sections];
                          next[sIdx] = {
                            ...next[sIdx],
                            parts: next[sIdx].parts.filter((_, i) => i !== pIdx),
                          };
                          setSections(next);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const next = [...sections];
                      next[sIdx] = {
                        ...next[sIdx],
                        parts: [...next[sIdx].parts, { label: "", text: "" }],
                      };
                      setSections(next);
                    }}
                  >
                    Add Part
                  </Button>
                </div>

                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const next = sections.filter((_, i) => i !== sIdx);
                    setSections(next);
                  }}
                >
                  Remove Section
                </Button>
              </div>
            ))}

            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setSections([
                  ...sections,
                  { number: sections.length + 1, title: "", duration: "", parts: [] },
                ]);
              }}
            >
              Add Outline Section
            </Button>
          </div>
        );
      },
      JsonField: ({
        element,
      }: {
        element: { props?: { label?: string; path?: string; rows?: number } };
      }) => {
        const path = element.props?.path ?? "";
        const current = getByPath(editorLessonRef.current, path);
        const fallback =
          current === undefined ? "" : JSON.stringify(current, null, 2);
        const value = jsonFieldDraftsRef.current[path] ?? fallback;
        return (
          <div className="space-y-1">
            <p className="text-xs font-medium">{element.props?.label}</p>
            <Textarea
              className="font-mono text-xs"
              value={value}
              rows={element.props?.rows}
              onChange={(event) =>
                setJsonFieldDrafts((prev) => {
                  const nextDrafts = {
                    ...prev,
                    [path]: event.target.value,
                  };
                  jsonFieldDraftsRef.current = nextDrafts;
                  return nextDrafts;
                })
              }
              onBlur={() => {
                if (!value.trim()) return;
                applyJsonDraft(path, value);
              }}
            />
          </div>
        );
      },
    }),
    [],
  );

  const saveLesson = async () => {
    if (!editorLesson) {
      setAdminStatus("No lesson loaded in editor.");
      return;
    }
    let payload = editorLesson;

    for (const [path, value] of Object.entries(jsonFieldDrafts)) {
      if (!value.trim()) continue;
      try {
        const parsed = JSON.parse(value);
        payload = setByPath(
          payload as Record<string, unknown>,
          path,
          parsed,
        ) as MiniLesson;
      } catch {
        setAdminStatus(`Invalid JSON for field: ${path}`);
        return;
      }
    }

    if (!payload?.id?.trim() || !payload?.title?.trim()) {
      setAdminStatus("Lesson must include non-empty `id` and `title`.");
      return;
    }

    if (editingLessonId && payload.id !== editingLessonId) {
      setAdminStatus("Cannot change lesson id when editing.");
      return;
    }

    setIsSavingLesson(true);
    setAdminStatus(null);
    try {
      const isEditing = Boolean(editingLessonId);
      const endpoint = isEditing
        ? `/api/mini-lessons/${encodeURIComponent(editingLessonId as string)}`
        : "/api/mini-lessons";
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = (await response.json().catch(() => ({}))) as {
        error?: string;
      };

      if (!response.ok) {
        setAdminStatus(result.error ?? "Failed to save mini lesson.");
        return;
      }

      await refreshMiniLessons();
      setIsEditorOpen(false);
      setAdminStatus(isEditing ? "Mini lesson updated." : "Mini lesson created.");
    } catch {
      setAdminStatus("Failed to save mini lesson.");
    } finally {
      setIsSavingLesson(false);
    }
  };

  return (
    <div className="flex flex-col h-full p-4 bg-[#7DDBBF] relative overflow-hidden">
      {/* Decorative blob shapes in background */}
      <div className="absolute -top-16 -right-16 w-40 h-40 bg-[var(--mindup-pink)]/15 blob-shape pointer-events-none" />
      <div className="absolute bottom-20 -left-10 w-32 h-32 bg-[var(--mindup-deep-green)]/8 blob-shape-2 pointer-events-none" />
      
      {/* Floating animated brand icons */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[30%] right-3 w-7 h-7 rounded-lg bg-[var(--mindup-soft-black)]/5 p-1 opacity-40 animate-[float_5s_ease-in-out_infinite]">
          <Image src="/brand/icons/sparkle.gif" alt="" width={20} height={20} className="w-full h-full" unoptimized />
        </div>
        <div className="absolute bottom-[35%] left-2 w-6 h-6 rounded-lg bg-[var(--mindup-soft-black)]/5 p-1 opacity-35 animate-[float_7s_ease-in-out_infinite_1s]">
          <Image src="/brand/icons/flower.gif" alt="" width={16} height={16} className="w-full h-full" unoptimized />
        </div>
        <div className="absolute bottom-[15%] right-4 w-6 h-6 rounded-lg bg-[var(--mindup-soft-black)]/5 p-1 opacity-35 animate-[float_6s_ease-in-out_infinite_2s]">
          <Image src="/brand/icons/stars.gif" alt="" width={16} height={16} className="w-full h-full" unoptimized />
        </div>
      </div>

      <div className="mb-6 relative z-10">
        <Image
          src="/brand/logos/MU_PrimaryLogo_RGB.png"
          alt="MindUP"
          width={120}
          height={32}
          className="h-8 w-auto"
        />
        <p className="text-xs text-[var(--mindup-soft-black)]/75 mt-2">Explore the full MindUP curriculum, including 15-minute mini lessons and longer full-length lessons. Click a lesson to view it on the right.</p>
        {isAdmin && (
          <a
            href="/?landing=1"
            onClick={(event) => {
              event.preventDefault();
              window.location.assign("/?landing=1");
            }}
            className="mt-2 inline-block text-xs font-medium text-[var(--mindup-soft-black)]/80 underline underline-offset-2 hover:text-[var(--mindup-soft-black)]"
          >
            Edit landing page
          </a>
        )}
      </div>

      {/* Search */}
      <div className="mb-4 relative z-10">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--mindup-soft-black)]/50" />
          <Input
            placeholder="Search lessons..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8 h-9 text-sm bg-white/80 border-[var(--mindup-soft-black)]/15 text-[var(--mindup-soft-black)] placeholder:text-[var(--mindup-soft-black)]/50 focus:border-[var(--mindup-deep-orange)]/50 focus:ring-[var(--mindup-deep-orange)]/20"
          />
        </div>
      </div>

      {/* Mini lessons */}
      <div className="mb-4 relative z-10 shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <Folder className="w-4 h-4 text-[var(--mindup-deep-green)]" />
          <h2 className="font-display text-base font-bold text-[var(--mindup-soft-black)]">Mini Lessons</h2>
        </div>

        <div className="space-y-0.5">
          {filteredMiniLessons.map((content) => (
            <div
              key={content.id}
              className={cn(
                "w-full rounded-md transition-all flex items-center gap-1",
                currentMiniLesson?.id === content.id
                  ? "bg-[var(--mindup-plum)]/15"
                  : "hover:bg-[var(--mindup-plum)]/10"
              )}
            >
              <button
                onClick={() => handleMiniLessonClick(content)}
                className={cn(
                  "flex-1 text-left px-2.5 py-1.5 text-sm rounded-md transition-all",
                  currentMiniLesson?.id === content.id
                    ? "text-[var(--mindup-plum)] font-medium"
                    : "text-[var(--mindup-soft-black)]/70 hover:text-[var(--mindup-soft-black)]"
                )}
              >
                {content.title}
              </button>
              {isAdmin && (
                <div className="mr-1 flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => openEditDialog(content)}
                    className="h-6 w-6 rounded hover:bg-white/80 inline-flex items-center justify-center text-[var(--mindup-soft-black)]/75 hover:text-[var(--mindup-soft-black)]"
                    aria-label={`Edit ${content.title}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={isDeletingLessonId === content.id}
                    onClick={() => deleteMiniLesson(content)}
                    className="h-6 w-6 rounded hover:bg-white/80 inline-flex items-center justify-center text-[var(--mindup-soft-black)]/75 hover:text-red-600 disabled:opacity-50"
                    aria-label={`Delete ${content.title}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
          {filteredMiniLessons.length === 0 && (
            <div className="px-2.5 py-1.5 text-sm text-[var(--mindup-soft-black)]/50 italic">
              No mini lessons match your search
            </div>
          )}
          {miniLessonActionStatus && (
            <p className="px-2.5 pt-2 text-xs text-[var(--mindup-soft-black)]/70">
              {miniLessonActionStatus}
            </p>
          )}
        </div>
      </div>

      <Dialog open={isEditorOpen} onOpenChange={setIsEditorOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingLessonId ? "Edit mini lesson" : "Create mini lesson"}</DialogTitle>
            <DialogDescription>
              Required field: <code>title</code>.
            </DialogDescription>
          </DialogHeader>
          {editorLesson && editorSpec && (
            <div className="max-h-[60vh] overflow-auto space-y-3 pr-1">
              <JSONUIProvider registry={editorRegistry}>
                <Renderer spec={editorSpec} registry={editorRegistry} />
              </JSONUIProvider>
            </div>
          )}
          {adminStatus && (
            <p className="text-xs text-red-600">{adminStatus}</p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditorOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveLesson} disabled={!isAdmin || isSavingLesson}>
              {isSavingLesson ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full-length Lessons */}
      <div className="mb-4 relative z-10 shrink-0">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="w-4 h-4 text-[var(--mindup-deep-green)]" />
          <h2 className="font-display text-base font-bold text-[var(--mindup-soft-black)]">Full-Length Lessons</h2>
        </div>
      </div>

      {/* TOC Section */}
      <div className="mb-6 relative z-10 flex-1 overflow-hidden flex flex-col">
        <div className="flex-1 overflow-auto space-y-2 pr-1">
          {GRADE_ORDER.map((grade) => {
            const gradeExpanded = expandedGrades.has(grade);
            const gradeUnits = filteredLessonsByGradeAndUnit[grade];

            return (
              <div key={grade} className="mb-1">
                <button
                  onClick={() => toggleGrade(grade)}
                  className={cn(
                    "w-full text-left px-2.5 py-2 text-sm font-semibold rounded-lg transition-all flex items-center justify-between gap-2 min-w-0",
                    "hover:bg-[var(--mindup-plum)]/10",
                    "text-[var(--mindup-soft-black)]",
                    gradeExpanded && "bg-[var(--mindup-plum)]/10"
                  )}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    {gradeExpanded ? (
                      <ChevronDown size={16} strokeWidth={2.5} className="shrink-0 text-[var(--mindup-plum)]" />
                    ) : (
                      <ChevronRight size={16} strokeWidth={2.5} className="shrink-0 text-[var(--mindup-plum)]" />
                    )}
                    <span className="truncate">{grade}</span>
                  </span>
                  <span className="text-xs text-[var(--mindup-soft-black)]/60">{lessonCountsByGrade[grade]}</span>
                </button>

                {gradeExpanded && (
                  <div className="ml-3 mt-1 space-y-1 border-l-2 border-[var(--mindup-plum)]/40 pl-3">
                    {!hasGradeLessons(grade) ? (
                      <div className="px-2 py-1 text-sm text-[var(--mindup-soft-black)]/50 italic">No lessons loaded yet</div>
                    ) : (
                      Object.entries(gradeUnits)
                        .sort(([a], [b]) => Number(a) - Number(b))
                        .map(([unitNum, { unitTitle, lessons }]) => {
                          const unit = Number(unitNum);
                          const unitExpanded = isUnitExpanded(grade, unit);

                          return (
                            <div key={`${grade}-${unit}`} className="mb-1">
                              <button
                                onClick={() => toggleUnit(grade, unit)}
                                className={cn(
                                  "w-full text-left px-2.5 py-1.5 text-sm font-medium rounded-lg transition-all flex items-center gap-2 min-w-0",
                                  "hover:bg-[var(--mindup-plum)]/10",
                                  "text-[var(--mindup-soft-black)]",
                                  unitExpanded && "bg-[var(--mindup-plum)]/10"
                                )}
                              >
                                <span className="flex items-center justify-center w-4 min-w-4 shrink-0">
                                  {unitExpanded ? (
                                    <ChevronDown size={14} strokeWidth={2.5} className="shrink-0 text-[var(--mindup-plum)]" />
                                  ) : (
                                    <ChevronRight size={14} strokeWidth={2.5} className="shrink-0 text-[var(--mindup-plum)]" />
                                  )}
                                </span>
                                <span className="truncate">Unit {unit}: {unitTitle}</span>
                              </button>

                              {unitExpanded && (
                                <div className="ml-3 mt-1 space-y-0.5 border-l-2 border-[var(--mindup-plum)]/35 pl-3">
                                  {lessons
                                    .sort((a, b) => a.lessonNumber - b.lessonNumber)
                                    .map((lesson) => (
                                      <button
                                        key={lesson.id}
                                        onClick={() => handleTocLessonClick(lesson)}
                                        className={cn(
                                          "w-full text-left px-3 py-1.5 text-sm rounded-md transition-all truncate",
                                          "text-[var(--mindup-soft-black)]/70 hover:text-[var(--mindup-soft-black)] hover:bg-[var(--mindup-plum)]/10",
                                          selectedTocLesson === lesson.id &&
                                            "bg-[var(--mindup-plum)]/15 text-[var(--mindup-plum)] font-medium border-l-[3px] border-[var(--mindup-plum)] -ml-[2px] pl-[13px]"
                                        )}
                                      >
                                        {lesson.lessonNumber}. {lesson.title}
                                      </button>
                                    ))}
                                </div>
                              )}
                            </div>
                          );
                        })
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}






